/* Dhikr counter — count istighfār, tasbīḥ and the rest through the day, with
   today / week / 2-week / month totals per dhikr (Monday-based calendar weeks). Counts sync with the account. */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { BookOpenText, Check, ChevronLeft, ChevronRight, Minus, Plus, RotateCcw, Target, Trash2 } from 'lucide-react';
import { addDays, dayKeyIn } from '@shared/prayer';
import { useProgress, useSettings } from '@/lib/store';
import type { CustomDhikr } from '@/lib/types';
import { IconButton, LargeTitle, Section, Sheet, haptic } from '@/components/ui';
import { HadithList, hadithIdsFor } from './Virtues';

export interface DhikrDef { id: string; ar: string; tr: string; en: string; custom?: boolean }

/* The common dhikr. Ṣalawāt includes his family (wa ʿalā āli Muḥammad). */
export const PRESET_DHIKR: DhikrDef[] = [
  { id: 'istighfar', ar: 'أَسْتَغْفِرُ اللَّهَ', tr: 'Astaghfirullāh', en: 'I seek Allah’s forgiveness' },
  { id: 'istighfar-tawbah', ar: 'أَسْتَغْفِرُ اللَّهَ وَأَتُوبُ إِلَيْهِ', tr: 'Astaghfirullāha wa atūbu ilayh', en: 'I seek Allah’s forgiveness and turn to Him in repentance' },
  { id: 'subhanallah', ar: 'سُبْحَانَ اللَّهِ', tr: 'Subḥān Allāh', en: 'Glory be to Allah' },
  { id: 'alhamdulillah', ar: 'الْحَمْدُ لِلَّهِ', tr: 'Al-ḥamdu lillāh', en: 'All praise is for Allah' },
  { id: 'allahuakbar', ar: 'اللَّهُ أَكْبَرُ', tr: 'Allāhu akbar', en: 'Allah is the Greatest' },
  { id: 'tahlil', ar: 'لَا إِلَهَ إِلَّا اللَّهُ', tr: 'Lā ilāha illā Allāh', en: 'There is no god but Allah' },
  { id: 'subhanallah-wabihamdih', ar: 'سُبْحَانَ اللَّهِ وَبِحَمْدِهِ', tr: 'Subḥān Allāhi wa bi-ḥamdih', en: 'Glory and praise be to Allah' },
  { id: 'hawqala', ar: 'لَا حَوْلَ وَلَا قُوَّةَ إِلَّا بِاللَّهِ', tr: 'Lā ḥawla wa lā quwwata illā billāh', en: 'There is no power and no strength except with Allah' },
  { id: 'salawat', ar: 'اللَّهُمَّ صَلِّ عَلَى مُحَمَّدٍ وَعَلَى آلِ مُحَمَّدٍ', tr: 'Allāhumma ṣalli ʿalā Muḥammad wa ʿalā āli Muḥammad', en: 'O Allah, send blessings upon Muḥammad and the family of Muḥammad' },
];

const TARGETS = [33, 99, 100, 1000, 0] as const; // 0 = no target
/* Stable fallbacks — a fresh {} / [] in a store selector re-renders forever. */
const NO_CUSTOM: CustomDhikr[] = [];
const NO_LOG: Record<string, { counts: Record<string, number>; last?: string }> = {};
const NO_COUNTS: Record<string, number> = {};
const NO_GOALS: Record<string, number> = {};
const MAX_GOAL = 100_000;
const GOAL_PRESETS = [100, 300, 500, 1000, 3000];
const targetKey = (id: string) => `ftu_dhikr_target_${id}`;
const readTarget = (id: string) => { try { return Number(localStorage.getItem(targetKey(id)) ?? (id === 'istighfar' ? 100 : 33)); } catch { return 33; } };

function useToday() { return dayKeyIn(useSettings((s) => s.settings.prayer.tz)); }

/** Your daily goal for a dhikr (0 = none). Synced with your settings. */
export const useDhikrGoal = (id: string) => useSettings((s) => s.settings.dhikrGoals?.[id] ?? 0);
function setDhikrGoal(id: string, n: number) {
  const { settings, update } = useSettings.getState();
  const goals = { ...(settings.dhikrGoals ?? {}) };
  if (n > 0) goals[id] = n; else delete goals[id];
  update({ dhikrGoals: goals });
}

export function useAllDhikr(): DhikrDef[] {
  const custom = useSettings((s) => s.settings.customDhikr ?? NO_CUSTOM);
  return useMemo(() => [...PRESET_DHIKR, ...custom.map((c) => ({ ...c, custom: true }))], [custom]);
}

/** Totals for one dhikr (or all, without id), by calendar: today; this week (Monday to today);
    two weeks (last Monday to today); this month (the 1st to today). */
export function useDhikrTotals(id?: string) {
  const log = useProgress((s) => s.dhikr ?? NO_LOG);
  const today = useToday();
  return useMemo(() => {
    const count = (d?: { counts: Record<string, number> }) => (d ? (id ? d.counts[id] ?? 0 : Object.values(d.counts).reduce((a, b) => a + b, 0)) : 0);
    const span = (days: number) => { let n = 0; for (let i = 0; i < days; i++) n += count(log[addDays(today, -i)]); return n; };
    const sinceMonday = (new Date(`${today}T12:00:00Z`).getUTCDay() + 6) % 7;   // 0 on a Monday … 6 on a Sunday
    return { today: count(log[today]), week: span(sinceMonday + 1), fortnight: span(sinceMonday + 8), month: span(Number(today.slice(8))) };
  }, [log, today, id]);
}

type Totals = ReturnType<typeof useDhikrTotals>;
const totalCells = (t: Totals) => [['Today', t.today], ['Week', t.week], ['2 weeks', t.fortnight], ['Month', t.month]] as const;

/** The dhikr counted most recently (on any day), with today's count and its target. */
export function useLastDhikr() {
  const log = useProgress((s) => s.dhikr ?? NO_LOG);
  const all = useAllDhikr();
  const goals = useSettings((s) => s.settings.dhikrGoals ?? NO_GOALS);
  const today = useToday();
  return useMemo(() => {
    for (const k of Object.keys(log).sort().reverse()) {
      const d = log[k].last && all.find((x) => x.id === log[k].last);
      // Your daily goal if you set one, otherwise the round size you count to.
      if (d) return { dhikr: d, today: log[today]?.counts[d.id] ?? 0, target: goals[d.id] || readTarget(d.id) };
    }
    return null;
  }, [log, all, goals, today]);
}

const fmt = (n: number) => n.toLocaleString('en-GB');

export function DhikrList() {
  const nav = useNavigate();
  const all = useAllDhikr();
  const today = useToday();
  const todayCounts = useProgress((s) => s.dhikr?.[today]?.counts ?? NO_COUNTS);
  const goals = useSettings((s) => s.settings.dhikrGoals ?? NO_GOALS);
  const totals = useDhikrTotals();
  const [adding, setAdding] = useState(false);
  return (
    <div className="page-pad">
      <LargeTitle back="/adhkar" title="Dhikr" ar="الذكر" subtitle="Counter" right={<IconButton label="Add a dhikr" onClick={() => setAdding(true)}><Plus size={20} /></IconButton>} />
      <div className="grid grid-cols-4 gap-2 px-4 pb-4">
        {totalCells(totals).map(([l, n]) => (
          <div key={l} className="rounded-[20px] bg-surface px-1 py-3 text-center">
            <div className="text-[12px] font-semibold uppercase tracking-wide text-muted">{l}</div>
            <div className="mt-1 text-[22px] font-bold tabular-nums">{fmt(n)}</div>
          </div>
        ))}
      </div>
      <div className="mx-4 overflow-hidden rounded-[22px] bg-surface">
        {all.map((d) => (
          <button key={d.id} type="button" onClick={() => nav(`/adhkar/dhikr/${d.id}`)} className="press flex w-full items-center gap-3 border-b border-hairline px-4 py-3 text-left last:border-0">
            <span className="min-w-0 flex-1">
              <span className="block truncate font-arabic text-[20px] leading-snug" dir="rtl">{d.ar}</span>
              <span className="block truncate text-[13px] text-muted">{d.tr}</span>
            </span>
            <span className="shrink-0 text-right">
              <span className="block text-[17px] font-bold tabular-nums">{fmt(todayCounts[d.id] ?? 0)}{goals[d.id] ? <span className="text-[12px] font-medium text-muted"> / {fmt(goals[d.id])}</span> : null}</span>
              <span className={`block text-[11px] ${goals[d.id] && (todayCounts[d.id] ?? 0) >= goals[d.id] ? 'font-semibold text-accent' : 'text-muted'}`}>{goals[d.id] && (todayCounts[d.id] ?? 0) >= goals[d.id] ? 'goal reached ✓' : 'today'}</span>
            </span>
            <ChevronRight size={18} className="shrink-0 text-faint" />
          </button>
        ))}
      </div>
      <AddDhikrSheet open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}

function AddDhikrSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const custom = useSettings((s) => s.settings.customDhikr ?? NO_CUSTOM);
  const update = useSettings((s) => s.update);
  const [ar, setAr] = useState(''), [tr, setTr] = useState(''), [en, setEn] = useState('');
  const input = 'w-full rounded-xl bg-fill px-4 py-3 text-[16px] outline-none focus:ring-2 focus:ring-accent';
  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ar.trim() && !tr.trim()) return;
    const d: CustomDhikr = { id: `c-${Date.now().toString(36)}`, ar: ar.trim(), tr: tr.trim() || ar.trim(), en: en.trim() };
    update({ customDhikr: [...custom, d] });
    setAr(''); setTr(''); setEn('');
    onClose();
  };
  return (
    <Sheet open={open} onClose={onClose} title="Add a dhikr">
      <form onSubmit={save} className="space-y-3 px-5 pb-2">
        <input className={`${input} font-arabic text-[20px]`} dir="rtl" lang="ar" placeholder="Arabic (optional)" aria-label="Arabic" value={ar} onChange={(e) => setAr(e.target.value)} />
        <input className={input} placeholder="Transliteration, e.g. Subḥān Allāh" aria-label="Transliteration" value={tr} onChange={(e) => setTr(e.target.value)} />
        <input className={input} placeholder="Meaning (optional)" aria-label="Meaning" value={en} onChange={(e) => setEn(e.target.value)} />
        <button type="submit" disabled={!ar.trim() && !tr.trim()} className="press w-full rounded-2xl bg-accent py-3.5 text-[17px] font-semibold text-accent-ink disabled:opacity-50">Add</button>
      </form>
    </Sheet>
  );
}

/** A sheet to type any number (a goal, a round size), with quick picks and a way to clear it. */
function NumberSheet({ open, onClose, title, hint, value, presets, saveLabel, clearLabel, onSave }: {
  open: boolean; onClose: () => void; title: string; hint: string; value: number; presets: number[];
  saveLabel: string; clearLabel: string; onSave: (n: number) => void;
}) {
  const [text, setText] = useState('');
  useEffect(() => { if (open) setText(value > 0 ? String(value) : ''); }, [open, value]);
  const n = Number(text.replace(/[^0-9]/g, ''));
  const ok = n >= 1 && n <= MAX_GOAL;
  const submit = (e: React.FormEvent) => { e.preventDefault(); if (ok) { onSave(n); onClose(); } };
  return (
    <Sheet open={open} onClose={onClose} title={title}>
      <form onSubmit={submit} className="space-y-3 px-5 pb-2">
        <p className="text-[14px] text-muted">{hint}</p>
        <input className="w-full rounded-xl bg-fill px-4 py-3 text-center text-[28px] font-bold tabular-nums outline-none focus:ring-2 focus:ring-accent"
          inputMode="numeric" pattern="[0-9]*" aria-label={title} placeholder="e.g. 250" value={text}
          onChange={(e) => setText(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))} />
        <div className="flex flex-wrap justify-center gap-2" role="group" aria-label="Quick picks">
          {presets.map((p) => (
            <button key={p} type="button" onClick={() => setText(String(p))} className={`press rounded-full px-3.5 py-1.5 text-[13px] font-semibold ${n === p ? 'bg-accent text-accent-ink' : 'bg-fill'}`}>{fmt(p)}</button>
          ))}
        </div>
        {n > MAX_GOAL && <p className="text-center text-[13px] text-[var(--win-warn)]">Up to {fmt(MAX_GOAL)}.</p>}
        <button type="submit" disabled={!ok} className="press w-full rounded-2xl bg-accent py-3.5 text-[17px] font-semibold text-accent-ink disabled:opacity-50">{saveLabel}</button>
        {value > 0 && <button type="button" onClick={() => { onSave(0); onClose(); }} className="press w-full rounded-2xl bg-fill py-3 text-[15px] font-semibold text-muted">{clearLabel}</button>}
      </form>
    </Sheet>
  );
}

export function DhikrCounterPage() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const all = useAllDhikr();
  const d = all.find((x) => x.id === id);
  const today = useToday();
  const add = useProgress((s) => s.addDhikr);
  const totals = useDhikrTotals(id);
  const [target, setTargetState] = useState(() => readTarget(id));
  const [session, setSession] = useState(0);
  const [goalOpen, setGoalOpen] = useState(false);
  const [roundOpen, setRoundOpen] = useState(false);
  const [virtuesOpen, setVirtuesOpen] = useState(false);
  const goal = useDhikrGoal(id);
  const custom = useSettings((s) => s.settings.customDhikr ?? NO_CUSTOM);
  const update = useSettings((s) => s.update);
  if (!d) return <div className="page-pad px-6 pt-20 text-center text-muted">Not found. <Link to="/adhkar/dhikr" className="font-semibold text-accent">All dhikr</Link></div>;

  const setTarget = (t: number) => { setTargetState(t); try { localStorage.setItem(targetKey(id), String(t)); } catch {} };
  const reached = target > 0 && session >= target;
  const goalMet = goal > 0 && totals.today >= goal;
  const tap = () => {
    const next = session + 1;
    // A stronger buzz at the target (and at every full round after it) — and the strongest when today's goal is met.
    haptic(goal > 0 && totals.today + 1 === goal ? 80 : target > 0 && next % target === 0 ? 45 : 8);
    setSession(next);
    add(today, id, 1);
  };
  const undo = () => { if (session <= 0) return; haptic(6); setSession(session - 1); add(today, id, -1); };
  const pct = target > 0 ? Math.min(1, session / target) : 0;

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="glass sticky top-0 z-30 flex items-center gap-2 px-3 pb-2" style={{ paddingTop: 'calc(var(--safe-top) + 8px)' }}>
        <IconButton label="Back" onClick={() => nav('/adhkar/dhikr')}><ChevronLeft size={20} /></IconButton>
        <div className="min-w-0 flex-1 text-center text-[15px] font-semibold">{d.tr}</div>
        {d.custom ? (
          <IconButton label="Delete this dhikr" onClick={() => { if (confirm('Remove this dhikr from the list? Its past counts stay in your totals.')) { update({ customDhikr: custom.filter((c) => c.id !== id) }); nav('/adhkar/dhikr'); } }}><Trash2 size={17} /></IconButton>
        ) : <span className="w-10" />}
      </header>

      {/* The whole middle is the tap target — count without looking. */}
      <button type="button" onClick={tap} aria-label={`Count ${d.tr}`} className="flex flex-1 select-none flex-col items-center justify-center gap-4 px-6 py-6 text-center active:bg-fill/40">
        {d.ar && <span className="font-arabic text-[34px] leading-[1.9]" dir="rtl" lang="ar">{d.ar}</span>}
        {d.en && <span className="max-w-[420px] text-[15px] text-muted">{d.en}</span>}
        <span className="relative mt-2 grid h-44 w-44 place-items-center">
          <svg viewBox="0 0 100 100" className="absolute inset-0 -rotate-90" aria-hidden>
            <circle cx="50" cy="50" r="45" fill="none" strokeWidth="6" className="stroke-fill" />
            {target > 0 && <circle cx="50" cy="50" r="45" fill="none" strokeWidth="6" strokeLinecap="round" className="stroke-accent transition-[stroke-dashoffset] duration-200" strokeDasharray={2 * Math.PI * 45} strokeDashoffset={(1 - pct) * 2 * Math.PI * 45} />}
          </svg>
          <span>
            <span className="block text-[46px] font-bold tabular-nums leading-none" aria-live="polite">{fmt(session)}</span>
            <span className="mt-1 block text-[13px] text-muted">{target > 0 ? (reached ? 'Target reached ✓' : `of ${target}`) : 'no target'}</span>
          </span>
        </span>
        <span className="text-[13px] text-faint">Tap anywhere to count</span>
      </button>

      <div className="px-4" style={{ paddingBottom: 'calc(var(--tabbar-h) + var(--safe-bottom) + 24px)' }}>
        <div className="mb-3 flex items-center justify-center gap-2">
          <button type="button" onClick={undo} disabled={session <= 0} aria-label="Undo one" className="press flex items-center gap-1 rounded-full bg-fill px-3 py-2 text-[13px] font-semibold disabled:opacity-40"><Minus size={14} /> 1</button>
          <button type="button" onClick={() => { setSession(0); haptic(); }} aria-label="New round" className="press flex items-center gap-1 rounded-full bg-fill px-3 py-2 text-[13px] font-semibold"><RotateCcw size={14} /> New round</button>
        </div>
        <button type="button" onClick={() => setGoalOpen(true)} aria-label={goal ? `Daily goal ${totals.today} of ${goal}` : 'Set a daily goal'} className="press mb-3 block w-full rounded-2xl bg-surface px-4 py-3 text-left">
          <span className="flex items-center justify-between gap-3 text-[14px]">
            <span className="flex items-center gap-2 font-semibold"><Target size={16} className="text-accent" /> {goal ? 'Daily goal' : 'Set a daily goal'}</span>
            {goal > 0 && <span className={`flex items-center gap-1 font-bold tabular-nums ${goalMet ? 'text-accent' : ''}`}>{goalMet && <Check size={15} />}{fmt(totals.today)} <span className="font-medium text-muted">/ {fmt(goal)}</span></span>}
          </span>
          {goal > 0 && <span className="mt-2 block h-1.5 overflow-hidden rounded-full bg-fill"><span className="block h-full rounded-full bg-accent transition-all" style={{ width: `${Math.min(100, (totals.today / goal) * 100)}%` }} /></span>}
        </button>
        {hadithIdsFor('dhikr', id).length > 0 && (
          <button type="button" onClick={() => { haptic(); setVirtuesOpen(true); }} className="press mb-3 flex w-full items-center gap-2 rounded-2xl bg-surface px-4 py-3 text-left text-[14px] font-semibold">
            <BookOpenText size={16} className="text-gold" /> <span className="flex-1">Virtues of this dhikr</span> <ChevronRight size={16} className="text-faint" />
          </button>
        )}
        {/* auto margins centre the chips when they fit and let them scroll from the start when they don't (justify-center clips the first one) */}
        <div className="no-scrollbar mb-3 flex gap-2 overflow-x-auto [&>*:first-child]:ml-auto [&>*:last-child]:mr-auto" role="radiogroup" aria-label="Round size">
          {TARGETS.map((t) => (
            <button key={t} type="button" role="radio" aria-checked={target === t} onClick={() => setTarget(t)}
              className={`press shrink-0 rounded-full px-3.5 py-1.5 text-[13px] font-semibold ${target === t ? 'bg-accent text-accent-ink' : 'bg-surface'}`}>{t || 'No target'}</button>
          ))}
          {!(TARGETS as readonly number[]).includes(target) && <button type="button" role="radio" aria-checked className="press shrink-0 rounded-full bg-accent px-3.5 py-1.5 text-[13px] font-semibold text-accent-ink" onClick={() => setRoundOpen(true)}>{target}</button>}
          <button type="button" onClick={() => setRoundOpen(true)} className="press shrink-0 rounded-full bg-surface px-3.5 py-1.5 text-[13px] font-semibold text-accent">Custom…</button>
        </div>
        <Section className="!mb-0">
          <div className="grid grid-cols-4 divide-x divide-hairline py-3 text-center">
            {totalCells(totals).map(([l, n]) => (
              <div key={l}><div className="text-[11px] font-semibold uppercase tracking-wide text-muted">{l}</div><div className="text-[18px] font-bold tabular-nums">{fmt(n)}</div></div>
            ))}
          </div>
        </Section>
      </div>

      <Sheet open={virtuesOpen} onClose={() => setVirtuesOpen(false)} title="Virtues">
        <div className="space-y-4 px-5 pb-4">
          <div className="text-center">
            {d.ar && <p className="font-arabic text-[26px] leading-[1.9]" dir="rtl" lang="ar">{d.ar}</p>}
            <p className="text-[14px] italic text-muted">{d.tr}</p>
          </div>
          <HadithList ids={hadithIdsFor('dhikr', id)} title="Hadith on its virtue" />
        </div>
      </Sheet>
      <NumberSheet open={goalOpen} onClose={() => setGoalOpen(false)} title="Daily goal" value={goal} presets={GOAL_PRESETS} saveLabel="Save goal" clearLabel="Remove goal"
        hint={`How many times a day you want to say ${d.tr}. Your count for today carries across all your devices.`} onSave={(n) => setDhikrGoal(id, n)} />
      <NumberSheet open={roundOpen} onClose={() => setRoundOpen(false)} title="Round size" value={TARGETS.includes(target as never) ? 0 : target} presets={[7, 10, 33, 40, 70, 99, 100, 313]} saveLabel="Use this round size" clearLabel="Back to a preset"
        hint="How many to count in one sitting before the ring fills. “New round” starts again." onSave={(n) => setTarget(n || 33)} />
    </div>
  );
}
